import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import { Field } from "./Employees";

export default function Departments() {
  const { user } = useAuth();
  const canManage = hasRole(user, "super_admin", "hr");
  const canDelete = hasRole(user, "super_admin");
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", code: "", description: "", department_head_id: null };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/departments").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/employees/lookup/all").then((r) => setEmployees(r.data)); }, []);

  const save = async () => {
    if (!form.name || !form.code) return toast.error("Name and code required");
    try {
      if (editing) await api.put(`/departments/${editing.id}`, form);
      else await api.post("/departments", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete ${it.name}? (Super Admin approval required)`)) return;
    try { await api.delete(`/departments/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="departments-page">
      <PageHeader
        title="Departments"
        description="Organisation structure & department heads."
        action={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="department-new-button" onClick={() => { setEditing(null); setForm(empty); }} className="btn-brand">
                <Plus size={16} className="mr-1.5" /> New Department
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Department</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                <Field label="Code *"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
                <Field label="Description"><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                <Field label="Department Head">
                  <Select value={form.department_head_id || "none"} onValueChange={(v) => setForm({ ...form, department_head_id: v === "none" ? null : v })}>
                    <SelectTrigger><SelectValue placeholder="Select head" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="btn-brand" data-testid="department-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />
      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Head</TableHead>
            <TableHead>Description</TableHead>{canManage && <TableHead className="text-right">Actions</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {items.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-medium">{d.name}</TableCell>
                <TableCell className="font-mono text-xs">{d.code}</TableCell>
                <TableCell>{employees.find((e) => e.id === d.department_head_id)?.full_name || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600 max-w-md truncate">{d.description || "—"}</TableCell>
                {canManage && (
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(d); setForm({ ...empty, ...d }); setOpen(true); }}>Edit</Button>
                    {canDelete && <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(d)}>Delete</Button>}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
